import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PublicIcon from "@mui/icons-material/Public";
import LockClockIcon from "@mui/icons-material/LockClock";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import useStyles from "./styles";
import {
  PoiSource,
  usePoiIngest,
  usePoiSources,
  usePoiTerritories,
} from "../../api/poi";

const DRIVER_LABELS: Record<string, string> = {
  file: "Fichier (CSV/XLSX/JSON)",
  opendatasoft: "Open data (ODS)",
  overpass: "OpenStreetMap",
  google_places: "Google Places",
  kbo: "Registre BCE/KBO",
  goca: "Scraping",
  nap_ocpi: "Point d'accès national (OCPI)",
};

export const SourcesTab: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { sources, isLoading } = usePoiSources();
  const { countries, groups, defaultCountry } = usePoiTerritories();

  const [dialogSource, setDialogSource] = useState<PoiSource | null>(null);
  const [country, setCountry] = useState<string>(defaultCountry);
  const [path, setPath] = useState("");
  const [since, setSince] = useState("");

  const ingest = usePoiIngest(() => setDialogSource(null));

  const countryOptions = useMemo(
    () => [
      ...groups.map((g) => ({ value: g, label: `${g} (groupe)` })),
      ...countries.map((c) => ({ value: c.code, label: `${c.code} — ${c.name}` })),
    ],
    [groups, countries]
  );

  const openDialog = (source: PoiSource) => {
    setDialogSource(source);
    // Pre-select a country the source actually covers, so the operator does not
    // have to discover by trial and error that kbo_be is Belgium-only.
    setCountry(
      source.is_eu_wide || source.countries.length === 0
        ? defaultCountry
        : source.countries[0]
    );
    setPath("");
    setSince("");
  };

  const runIngest = () => {
    if (!dialogSource) return;

    ingest.mutate({
      source: dialogSource.key,
      country,
      path: path || undefined,
      since: since || undefined,
    });
  };

  const isFileDriver = dialogSource?.driver === "file";

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t(
          "poi.sourcesIntro",
          "Chaque source déclare sa licence, son niveau de confiance et les pays qu'elle couvre. Une source à rétention limitée ne peut pas devenir propriétaire d'un champ stocké."
        )}
      </Typography>

      <TableContainer component={Paper} className={classes.tableWrap}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("poi.source", "Source")}</TableCell>
              <TableCell>{t("poi.driver", "Type")}</TableCell>
              <TableCell align="center">{t("poi.trust", "Confiance")}</TableCell>
              <TableCell>{t("poi.countries", "Pays")}</TableCell>
              <TableCell>{t("poi.licence", "Licence")}</TableCell>
              <TableCell>{t("poi.schedule", "Fréquence")}</TableCell>
              <TableCell align="right">{t("poi.records", "Fiches")}</TableCell>
              <TableCell align="right">{t("poi.actions", "Actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sources.map((source) => (
              <TableRow
                key={source.key}
                hover
                className={source.enabled ? undefined : classes.sourceRowDisabled}
              >
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {source.key}
                    </Typography>
                    {source.requires_key && (
                      <Tooltip
                        title={t("poi.needsKey", "Nécessite une clé/identifiants")}
                      >
                        <VpnKeyIcon sx={{ fontSize: 15, color: "text.disabled" }} />
                      </Tooltip>
                    )}
                  </Box>
                  {!source.enabled && (
                    <Typography variant="caption" color="text.disabled">
                      {t("poi.disabled", "désactivée dans config/poi.php")}
                    </Typography>
                  )}
                </TableCell>

                <TableCell>
                  <Typography variant="caption">
                    {DRIVER_LABELS[source.driver ?? ""] ?? source.driver}
                  </Typography>
                </TableCell>

                <TableCell align="center">
                  <Chip
                    size="small"
                    className={classes.trustChip}
                    label={source.trust ?? "—"}
                    color={
                      (source.trust ?? 0) >= 90
                        ? "success"
                        : (source.trust ?? 0) >= 65
                        ? "primary"
                        : "default"
                    }
                    variant="outlined"
                  />
                </TableCell>

                <TableCell>
                  {source.is_eu_wide ? (
                    <Chip
                      size="small"
                      icon={<PublicIcon />}
                      label={t("poi.euWide", "Toute l'UE")}
                      color="info"
                      variant="outlined"
                    />
                  ) : (
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {source.countries.map((c) => (
                        <Chip key={c} size="small" label={c} variant="outlined" />
                      ))}
                    </Box>
                  )}
                </TableCell>

                <TableCell>
                  {source.retention_days ? (
                    <Tooltip
                      title={t(
                        "poi.retentionTip",
                        "Contrainte de licence : ces données ne peuvent pas être conservées au-delà de cette durée. Seule la géométrie est stockée."
                      )}
                    >
                      <Chip
                        size="small"
                        icon={<LockClockIcon />}
                        className={classes.retentionWarning}
                        label={t("poi.retentionDays", {
                          days: source.retention_days,
                          defaultValue: `${source.retention_days} j max`,
                        })}
                      />
                    </Tooltip>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {t("poi.retentionNone", "Conservation libre")}
                    </Typography>
                  )}
                </TableCell>

                <TableCell>
                  <Typography variant="caption">{source.schedule ?? "—"}</Typography>
                </TableCell>

                <TableCell align="right">
                  <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {source.record_count.toLocaleString("fr-FR")}
                  </Typography>
                </TableCell>

                <TableCell align="right">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PlayArrowIcon />}
                    disabled={!source.enabled || ingest.isPending}
                    onClick={() => openDialog(source)}
                    sx={{ textTransform: "none" }}
                  >
                    {t("poi.run", "Lancer")}
                  </Button>
                </TableCell>
              </TableRow>
            ))}

            {sources.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className={classes.emptyState}>
                  {t("poi.noSources", "Aucune source configurée.")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Run dialog ─────────────────────────────────────────────────── */}
      <Dialog
        open={!!dialogSource}
        onClose={() => !ingest.isPending && setDialogSource(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("poi.runSource", "Lancer l'import")} — {dialogSource?.key}
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("poi.country", "Pays")}</InputLabel>
              <Select
                value={country}
                label={t("poi.country", "Pays")}
                onChange={(e) => setCountry(e.target.value)}
              >
                {countryOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {dialogSource && !dialogSource.is_eu_wide && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                {t("poi.countryScoped", {
                  countries: dialogSource.countries.join(", "),
                  defaultValue: `Cette source ne couvre que : ${dialogSource.countries.join(", ")}. Les autres pays sélectionnés seront ignorés.`,
                })}
              </Alert>
            )}

            {isFileDriver && (
              <TextField
                fullWidth
                size="small"
                label={t("poi.filePath", "Chemin du fichier sur le serveur")}
                placeholder="storage/app/imports/garages.csv"
                value={path}
                onChange={(e) => setPath(e.target.value)}
                helperText={t(
                  "poi.filePathHelp",
                  "CSV, TSV, XLSX, JSON ou JSONL. Le fichier doit déjà être déposé sur le serveur."
                )}
              />
            )}

            <TextField
              fullWidth
              size="small"
              type="date"
              label={t("poi.since", "Depuis (incrémental)")}
              InputLabelProps={{ shrink: true }}
              value={since}
              onChange={(e) => setSince(e.target.value)}
              helperText={t(
                "poi.sinceHelp",
                "Ignoré par les sources qui ne supportent pas l'incrémental."
              )}
            />

            {dialogSource?.retention_days && (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                {t(
                  "poi.retentionRunWarning",
                  "Source à rétention limitée : seules les coordonnées seront stockées. Les noms, téléphones et horaires ne sont pas conservés."
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDialogSource(null)}
            disabled={ingest.isPending}
            sx={{ textTransform: "none" }}
          >
            {t("common.cancel", "Annuler")}
          </Button>
          <Button
            variant="contained"
            onClick={runIngest}
            disabled={ingest.isPending || (isFileDriver && !path)}
            startIcon={
              ingest.isPending ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <PlayArrowIcon />
              )
            }
            sx={{ textTransform: "none" }}
          >
            {t("poi.startImport", "Démarrer")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
